/**
 * The sidebar (web-plan.md F1/F3): search, the All|Channels|Friends strip
 * (D179 — one component, active tab persisted, search filters the active
 * tab), the channel rows with unread badges, presence dots on DM rows only
 * (D194), and the dock with the account's own row.
 */
import { For, type JSX, Show, createMemo, createSignal } from "solid-js";

import { AvatarCircle } from "./avatar";
import { escapePending } from "../lib/escape";

import type { Dict } from "../lib/i18n";
import * as mut from "../store/mutators";
import type { FriendsState, PresenceState, Room, SidebarTab, UiState } from "../store/types";
import { isDmRoom } from "../store/types";
import { Icon, Ink, SearchField, TabStrip } from "./ds";

export function Sidebar(props: {
  state: UiState;
  t: Dict;
  onOpen(channelId: number): void;
  onTab(tab: SidebarTab): void;
  onCreateChannel(name: string): void;
  onOpenCard(userId: number): void;
  onAccept(userId: number): void;
  onDecline(userId: number): void;
}): JSX.Element {
  const [query, setQuery] = createSignal("");
  const [creating, setCreating] = createSignal(false);
  const [requestsOpen, setRequestsOpen] = createSignal(false);

  const visible = createMemo(() => {
    const tab = props.state.tab;
    const held = props.state.rooms.filter((r) =>
      tab === "all" ? true : tab === "channels" ? !isDmRoom(r) : isDmRoom(r),
    );
    const q = query().trim().toLowerCase();
    // An empty query returns the list unchanged — an empty RESULT then means
    // "no channels", while an empty result from a real query means "search
    // found nothing"; the two must stay distinguishable.
    if (q === "") return held;
    return held.filter((r) => r.name.toLowerCase().includes(q));
  });

  const incoming = () => props.state.friends.incoming.length;

  // A non-empty query on Friends folds in Directory matches under a caption
  // (D179) — deliberately the only way to find a stranger.
  const strangers = createMemo(() => {
    if (props.state.tab !== "friends") return [];
    const q = query().trim().toLowerCase();
    if (q === "") return [];
    const selfId = props.state.directory.selfId;
    return Object.entries(props.state.directory.people)
      .map(([id, p]) => ({ id: Number(id), p }))
      .filter(
        ({ id, p }) =>
          id !== selfId &&
          !props.state.friends.friends.some((f) => f.user_id === id) &&
          (p.username.toLowerCase().includes(q) ||
            (p.displayName ?? "").toLowerCase().includes(q)),
      );
  });

  return (
    <aside class="sidebar surface-pane surface-pane--column">
      <div class="sidebar__search">
        <SearchField
          value={query()}
          placeholder={props.t.searchPlaceholder()}
          onInput={setQuery}
        />
      </div>
      <div class="sidebar__tabs">
        <TabStrip
          tabs={[
            { key: "all", label: props.t.allTab() },
            { key: "channels", label: props.t.channels() },
            { key: "friends", label: props.t.friendsTab(), badge: incoming() },
          ]}
          selected={props.state.tab}
          onSelect={(key) => {
            // A second tap on Friends with requests waiting opens the list.
            if (key === "friends" && props.state.tab === "friends" && incoming() > 0) {
              setRequestsOpen(true);
            }
            props.onTab(key as SidebarTab);
          }}
        />
      </div>
      <Show when={props.state.tab === "friends" && incoming() > 0}>
        <button
          type="button"
          class="channel-row"
          style="margin:0 8px"
          onClick={() => setRequestsOpen(!requestsOpen())}
        >
          <span class="channel-row__face">
            <span class="avatar avatar--size-44" style="background-image:none">
              <Icon name="person_add" size={17} />
            </span>
          </span>
          <div class="channel-row__column">
            <div class="channel-row__top">
              <span class="channel-row__name">{props.t.requestsTitle()}</span>
              <span class="layout-spacer" />
              <span class="channel-row__badge">{incoming()}</span>
            </div>
          </div>
        </button>
        <Show when={requestsOpen()}>
          <For each={props.state.friends.incoming}>
            {(req) => {
              const p = props.state.directory.people[req.user_id];
              const name = p?.displayName || p?.username || `#${req.user_id}`;
              return (
                <div class="channel-row" style="margin:0 8px;cursor:default">
                  <span class="channel-row__face" role="button" onClick={() => props.onOpenCard(req.user_id)}>
                    <AvatarCircle
                      name={name}
                      createdAt={p?.createdAt ?? 0}
                      size={44}
                      fileId={p?.avatarFileId ?? null}
                    />
                  </span>
                  <div class="channel-row__column">
                    <div class="channel-row__top">
                      <span class="channel-row__name">{name}</span>
                      <span class="layout-spacer" />
                      <button
                        type="button"
                        class="echo-button echo-button--small echo-button--prominent"
                        onClick={() => props.onAccept(req.user_id)}
                      >
                        <Icon name="check" size={15} />
                      </button>
                      <button
                        type="button"
                        class="echo-button echo-button--small echo-button--secondary"
                        onClick={() => props.onDecline(req.user_id)}
                      >
                        <Icon name="close" size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            }}
          </For>
        </Show>
      </Show>
      {/* The fill behind the selected row is a mark of its own that rolls
          from row to row (`ds.Ink`), so the list is its host. */}
      <div class="sidebar__list" data-ink="true">
        <Ink class="sidebar__ink" />
        <Show when={creating()}>
          <div class="channel-row channel-row--new">
            <input
              class="channel-row__input"
              placeholder={props.t.newChannel()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const name = e.currentTarget.value.trim();
                  if (name !== "") props.onCreateChannel(name);
                  setCreating(false);
                }
                // The name being typed is given up by Escape — unless
                // something is open over it, whose key it is first.
                if (e.key === "Escape" && !escapePending()) setCreating(false);
              }}
            />
          </div>
        </Show>
        <For each={visible()}>
          {(room) => (
            <ChannelRow
              room={room}
              selected={props.state.selectedChannel === room.channelId}
              presence={props.state.presence}
              friends={props.state.friends}
              personOf={(id) => props.state.directory.people[id] ?? null}
              onOpen={() => props.onOpen(room.channelId)}
            />
          )}
        </For>
        <Show when={strangers().length > 0}>
          <span class="sidebar__caption">{props.t.searchPlaceholder()}</span>
          <For each={strangers()}>
            {({ id, p }) => (
              <button type="button" class="channel-row channel-row--person" onClick={() => props.onOpenCard(id)}>
                <span class="channel-row__face">
                  <AvatarCircle
                    name={p.displayName || p.username}
                    createdAt={p.createdAt}
                    size={44}
                    fileId={p.avatarFileId}
                  />
                </span>
                <div class="channel-row__column">
                  <div class="channel-row__top">
                    <span class="channel-row__name">{p.displayName || p.username}</span>
                  </div>
                </div>
              </button>
            )}
          </For>
        </Show>
        <Show when={props.state.tab !== "friends"}>
          <button type="button" class="channel-row channel-row--person" onClick={() => setCreating(true)}>
            <span class="channel-row__face">
              <span class="avatar avatar--size-44" style="background-image:none">
                <Icon name="add" size={17} />
              </span>
            </span>
            <div class="channel-row__column">
              <div class="channel-row__top">
                <span class="channel-row__name">{props.t.newChannel()}</span>
              </div>
            </div>
          </button>
        </Show>
      </div>
    </aside>
  );
}

function ChannelRow(props: {
  room: Room;
  selected: boolean;
  presence: PresenceState;
  friends: FriendsState;
  personOf: (id: number) => { createdAt: number; avatarFileId: string | null } | null;
  onOpen(): void;
}): JSX.Element {
  const r = () => props.room;
  const live = () => r().callMemberIds.length > 0;
  // Presence dots on DM rows only (D194), friends only, and the call mark
  // owns the corner when a call is live.
  const dotStatus = createMemo(() => {
    const peer = mut.dottedPeer(r(), props.friends);
    if (peer === null) return null;
    return mut.statusOf(props.presence, peer);
  });
  const face = createMemo(() => {
    if (isDmRoom(r()) && r().peerId !== null) {
      const p = props.personOf(r().peerId!);
      return { createdAt: p?.createdAt ?? 0, fileId: p?.avatarFileId ?? r().avatarFileId };
    }
    return { createdAt: 0, fileId: r().avatarFileId };
  });

  return (
    <button
      type="button"
      class={`channel-row${props.selected ? " channel-row--selected" : ""}${live() ? " channel-row--live" : ""}`}
      data-ink-here={props.selected ? "true" : undefined}
      onClick={() => props.onOpen()}
    >
      <span class="channel-row__face">
        <AvatarCircle
          name={r().name}
          createdAt={face().createdAt}
          size={44}
          fileId={face().fileId}
        />
        <Show when={live()}>
          <span class="call-mark">
            <Icon name="call" size={12} filled />
          </span>
        </Show>
        <Show when={dotStatus()}>
          {(status) => <span class={`presence-dot${dotClass(status())}`} />}
        </Show>
      </span>
      <div class="channel-row__column">
        <div class="channel-row__top">
          <span class="channel-row__name">{r().name}</span>
          <Show when={!isDmRoom(r()) && (r().settings.is_private || r().settings.has_password)}>
            <span class="channel-lock">
              <span class="channel-lock__mark">
                <Icon name={r().settings.is_private ? "lock" : "key"} size={12} />
              </span>
            </span>
          </Show>
          <span class="layout-spacer" />
          <span class="channel-row__time">{r().timeText}</span>
        </div>
        <div class="channel-row__bottom">
          <span class="channel-row__preview">{r().preview}</span>
          <Show when={r().unread > 0}>
            <span class="channel-row__badge">{r().unread > 99 ? "99+" : r().unread}</span>
          </Show>
        </div>
      </div>
    </button>
  );
}

function dotClass(status: string): string {
  switch (status) {
    case "afk":
      return " presence-dot--afk";
    case "dnd":
      return " presence-dot--dnd";
    case "online":
      return "";
    default:
      return " presence-dot--off";
  }
}
